import type { Edge, Node, XYPosition } from "@xyflow/react";
import type {
  CanvasEdgeData,
  CanvasNodeData,
  ChecklistItem,
  ISODateString,
} from "./model";

export type { ChecklistItem } from "./model";

export type CardColor =
  | "paper"
  | "butter"
  | "rose"
  | "mint"
  | "sky"
  | "lavender"
  | "graphite";

export interface SketchPoint {
  x: number;
  y: number;
}

export interface SketchStroke {
  id: string;
  color: string;
  width: number;
  points: SketchPoint[];
}

export interface EditorNodeData extends CanvasNodeData {
  title: string;
  color?: CardColor;
  borderRadius?: number;
  items?: ChecklistItem[];
  mimeType?: string;
  fileSize?: number;
  table?: string[][];
  strokes?: SketchStroke[];
  createdAt: ISODateString;
  updatedAt: ISODateString;
}

export type EditorNode = Node<EditorNodeData, "canvasNode">;
export type EditorEdge = Edge<CanvasEdgeData, "canvasEdge">;

export interface NodeRendererProps<TData extends EditorNodeData = EditorNodeData> {
  id: string;
  data: TData;
  selected: boolean;
  update: (patch: Partial<TData>, historyLabel?: string) => void;
}

export interface NodeDocumentWindowDefinition<TData extends EditorNodeData = EditorNodeData> {
  /** Rich document renderer hosted above the canvas viewport. */
  Renderer: React.ComponentType<NodeRendererProps<TData>>;
  /** Initial window size in canvas coordinates. */
  defaultSize: { width: number; height: number };
  /** Resize floor in canvas coordinates. */
  minSize?: { width: number; height: number };
  /** Short document format label shown in the generic titlebar. */
  kindLabel?: string;
  /** Optional title resolver. Defaults to the node title and then plug-in title. */
  getTitle?: (data: TData) => string;
}

/**
 * Interaction behavior declared by a component and enforced by the canvas host.
 *
 * New interaction switches belong here instead of becoming unrelated top-level
 * plug-in fields. This keeps the public contract discoverable as it grows.
 */
export interface NodeInteractionDeclaration {
  /**
   * Set to true only when the component renderer provides its own element with
   * the `drag-handle` class. Kimnote always renders a standard hover handle;
   * this declaration describes only the component's additional internal handle.
   */
  hasBuiltInDragHandle: boolean;
  /** Forward-compatible interaction declarations owned by future host versions. */
  readonly [name: string]: unknown;
}

export interface NodeAvailabilityDeclaration {
  /** False keeps legacy data renderable while removing the component from creation UI. */
  creatable: boolean;
  readonly [name: string]: unknown;
}

/** Extensible, host-readable declarations for a canvas component. */
export interface NodePluginDeclarations {
  interaction: NodeInteractionDeclaration;
  availability?: NodeAvailabilityDeclaration;
  /** Additional declaration groups should use a stable, descriptive namespace. */
  readonly [namespace: string]: unknown;
}

export interface NodePlugin<TData extends EditorNodeData = EditorNodeData> {
  type: string;
  version: number;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
    size?: number | string;
    strokeWidth?: number | string;
  }>;
  defaultSize: { width: number; height: number };
  declarations: NodePluginDeclarations;
  create: (overrides?: Partial<TData>) => TData;
  Renderer: React.MemoExoticComponent<React.ComponentType<NodeRendererProps<TData>>> | React.ComponentType<NodeRendererProps<TData>>;
  /** Optional reusable large-document surface supplied to the canvas window host. */
  documentWindow?: NodeDocumentWindowDefinition<TData>;
  capabilities?: {
    resize?: boolean;
    connect?: boolean;
    container?: boolean;
    textEdit?: boolean;
  };
  ports?: ReadonlyArray<{
    id: string;
    side: "top" | "right" | "bottom" | "left";
    label?: string;
  }>;
  validate?: (value: unknown) => TData;
  migrate?: (value: unknown, fromVersion: number) => TData;
  getAssetRefs?: (data: TData) => readonly string[];
  remapRefs?: (data: TData, idMap: ReadonlyMap<string, string>) => TData;
  toSearchText?: (data: TData) => string;
}

/** Type-safe identity helper for authoring components outside the core package. */
export function defineKimnoteComponent<TData extends EditorNodeData>(
  plugin: NodePlugin<TData>,
): NodePlugin<TData> {
  return plugin;
}

export interface ClipboardGraph {
  anchor: XYPosition;
  nodes: EditorNode[];
  edges: EditorEdge[];
}

export function createEditorNode(
  plugin: NodePlugin,
  position: XYPosition,
  overrides?: Partial<EditorNodeData>,
): EditorNode {
  const data = plugin.create(overrides);
  return {
    id: crypto.randomUUID(),
    type: "canvasNode",
    position,
    data,
    width: plugin.defaultSize.width,
    height: plugin.defaultSize.height,
    selected: false,
  };
}
